import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { phaseLabel } from './phaseGrid';

// Blocks, in the program designer.
//
// Without this the phase API is reachable only by hand-typing a URL: a coach
// could not create a block, and the grid — the whole point of the block — had no
// route into it. It is deliberately small. Creating a phase is four fields, and
// attaching a session to one is a select on the session card.
//
// The five strength qualities are the pipeline's SQ_CODES. The labels are the
// server's own (PhaseResponse.strengthQualityLabel); these are only for the
// create form, which has no phase to read a label from yet.
export const STRENGTH_QUALITIES = [
  { code: '', label: 'Unspecified' },
  { code: 'H', label: 'Hypertrophy' },
  { code: 'FH', label: 'Functional Hypertrophy' },
  { code: 'RS', label: 'Relative Strength' },
  { code: 'SE', label: 'Strength Endurance' },
  { code: 'ES', label: 'Energy System' },
];

export const EMPTY_PHASE_FORM = {
  name: '',
  phaseNumber: '',
  strengthQuality: '',
  blockLengthWeeks: '4',
};

/**
 * Validate a phase before it is sent.
 *
 * Mirrors models.TrainingPhase.Validate — the server is authoritative, this is
 * so a coach gets the answer without a round trip. Returns a map of field to
 * message, empty when valid.
 */
export function validatePhaseForm(form) {
  const errors = {};

  if (!String(form.name ?? '').trim()) {
    errors.name = 'Give the block a name.';
  }

  const number = Number(form.phaseNumber);
  if (!Number.isInteger(number) || number < 1 || number > 99) {
    errors.phaseNumber = 'The phase number must be between 1 and 99.';
  }

  const weeks = Number(form.blockLengthWeeks);
  if (!Number.isInteger(weeks) || weeks < 1 || weeks > 26) {
    errors.blockLengthWeeks = 'A block runs between 1 and 26 weeks.';
  }

  return errors;
}

/** The request body for creating a phase. */
export function buildPhaseRequest(form) {
  return {
    name: String(form.name ?? '').trim(),
    phaseNumber: Number(form.phaseNumber),
    strengthQuality: String(form.strengthQuality ?? ''),
    blockLengthWeeks: Number(form.blockLengthWeeks),
  };
}

/**
 * The phase number to suggest next: one past the highest that exists.
 *
 * Suggesting 1 every time makes the second block collide with the first, and the
 * server has no uniqueness rule to catch it — the number is a label, not a key.
 */
export function nextPhaseNumber(phases) {
  const highest = (phases ?? []).reduce(
    (max, phase) => Math.max(max, Number(phase.phaseNumber) || 0),
    0
  );
  return String(highest + 1);
}

const ProgramPhases = ({ programId, phases, onCreate, isSubmitting }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_PHASE_FORM);
  const [errors, setErrors] = useState({});

  const openForm = () => {
    setForm({ ...EMPTY_PHASE_FORM, phaseNumber: nextPhaseNumber(phases) });
    setErrors({});
    setShowForm(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const found = validatePhaseForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const created = await onCreate(buildPhaseRequest(form));
    // Only close on success, so a rejected block is still on screen to correct.
    if (created) {
      setShowForm(false);
      setForm(EMPTY_PHASE_FORM);
    }
  };

  const update = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  return (
    <section className="program-phases">
      <div className="program-phases-head">
        <h2 className="section-title">Blocks</h2>
        {!showForm && (
          <button type="button" className="btn-ghost" onClick={openForm}>
            Add block
          </button>
        )}
      </div>

      {(phases ?? []).length === 0 && !showForm && (
        <p className="meta-copy">
          No blocks yet. A block is a phase of training — four weeks of relative
          strength, say — and it is what the week-by-week grid is built from.
        </p>
      )}

      {(phases ?? []).length > 0 && (
        <ul className="phase-list">
          {phases.map((phase) => (
            <li key={phase.id} className="phase-list-item">
              <div>
                <strong>{phaseLabel(phase)}</strong>
                <span className="meta-copy">
                  {' '}{phase.blockLengthWeeks} week{phase.blockLengthWeeks === 1 ? '' : 's'}
                  {' · '}{phase.sessionCount} day{phase.sessionCount === 1 ? '' : 's'}
                </span>
              </div>
              {/* Two axes on one block, so both are offered from it. The week
                  reads across DAYS — is Thursday going to wreck what Tuesday
                  did. The grid reads across WEEKS — is this getting heavier. */}
              <span className="phase-actions">
                <Link className="btn-ghost" to={`/programs/${programId}/phases/${phase.id}/week`}>
                  Open week view
                </Link>
                <Link className="btn-ghost" to={`/programs/${programId}/phases/${phase.id}/grid`}>
                  Open grid
                </Link>
              </span>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <form className="phase-form" onSubmit={handleSubmit}>
          <div className="phase-form-row">
            <label htmlFor="phase-name">Name</label>
            <input
              id="phase-name"
              value={form.name}
              onChange={update('name')}
              aria-invalid={Boolean(errors.name)}
            />
            {errors.name && <span className="field-error">{errors.name}</span>}
          </div>

          <div className="phase-form-row">
            <label htmlFor="phase-number">Phase number</label>
            <input
              id="phase-number"
              type="number"
              value={form.phaseNumber}
              onChange={update('phaseNumber')}
              aria-invalid={Boolean(errors.phaseNumber)}
            />
            {errors.phaseNumber && <span className="field-error">{errors.phaseNumber}</span>}
          </div>

          <div className="phase-form-row">
            <label htmlFor="phase-quality">Strength quality</label>
            <select id="phase-quality" value={form.strengthQuality} onChange={update('strengthQuality')}>
              {STRENGTH_QUALITIES.map((quality) => (
                <option key={quality.code} value={quality.code}>
                  {quality.label}
                </option>
              ))}
            </select>
          </div>

          <div className="phase-form-row">
            <label htmlFor="phase-weeks">Weeks</label>
            <input
              id="phase-weeks"
              type="number"
              value={form.blockLengthWeeks}
              onChange={update('blockLengthWeeks')}
              aria-invalid={Boolean(errors.blockLengthWeeks)}
            />
            {errors.blockLengthWeeks && (
              <span className="field-error">{errors.blockLengthWeeks}</span>
            )}
          </div>

          <div className="phase-form-actions">
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Adding…' : 'Add block'}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default ProgramPhases;
